import { Helmet } from "react-helmet";
import { OG_IMAGE } from "./images";

const SITE_URL = "https://www.futuresmartlab.com";
const SITE_NAME = "Future Smart Lab";
const DEFAULT_DESC =
  "A premium technology, AI, STEM, leadership and entrepreneurship programme for children aged 6–18 in Nigeria. We raise builders, founders and innovators — not just coders.";

interface SeoProps {
  title?: string;
  description?: string;
  /** Path only, e.g. "/enroll" */
  path?: string;
  image?: string;
  /** Set true on private/utility pages (login, dashboards, thank-you) */
  noindex?: boolean;
  type?: "website" | "article";
}

export default function Seo({
  title,
  description = DEFAULT_DESC,
  path = "",
  image = OG_IMAGE,
  noindex = false,
  type = "website",
}: SeoProps) {
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} — Raising the next generation of innovators & founders`;
  const url = `${SITE_URL}${path}`;

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESC,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Abuja",
      addressCountry: "NG",
    },
    sameAs: [] as string[],
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta name="theme-color" content="#5b54c8" />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
    </Helmet>
  );
}
